import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Star, Award } from 'lucide-react';
import { useGamification } from '../context/GamificationContext';
import { GamificationNotification, BadgeTier } from '../types';
import { isGamificationMoment } from '../lib/gamificationMoments';

const TIER_COLORS: Record<BadgeTier, string> = {
  bronze: 'from-orange-400 to-amber-600',
  silver: 'from-gray-300 to-gray-500',
  gold: 'from-yellow-300 to-amber-500',
  platinum: 'from-indigo-400 to-purple-600',
};

const TIER_BORDER: Record<BadgeTier, string> = {
  bronze: 'border-orange-400',
  silver: 'border-gray-400',
  gold: 'border-yellow-400',
  platinum: 'border-indigo-400',
};

function ToastItem({ notification, onDismiss }: { notification: GamificationNotification; onDismiss: () => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    timerRef.current = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timerRef.current);
  }, [onDismiss]);

  const isLevelUp = notification.type === 'level_up';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      onClick={onDismiss}
      className={`cursor-pointer rounded-xl shadow-lg border overflow-hidden ${
        isLevelUp ? 'p-5 min-w-[280px]' : 'p-4 min-w-[260px]'
      } bg-white border-gray-200`}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        {notification.type === 'badge_earned' && notification.badge && (
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${TIER_COLORS[notification.badge.tier]} flex items-center justify-center flex-shrink-0`}>
            <Award className="w-5 h-5 text-white" />
          </div>
        )}
        {notification.type === 'streak_milestone' && (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center flex-shrink-0">
            <Flame className="w-5 h-5 text-white" />
          </div>
        )}
        {notification.type === 'level_up' && (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Star className="w-6 h-6 text-white" />
          </div>
        )}

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className={`font-bold text-gray-900 ${isLevelUp ? 'text-lg' : 'text-sm'}`}>
            {notification.title}
          </p>
          <p className={`text-gray-600 ${isLevelUp ? 'text-sm' : 'text-xs'}`}>
            {notification.message}
          </p>
        </div>

        {/* Badge tier indicator */}
        {notification.badge && (
          <div className={`w-2 h-10 rounded-full bg-gradient-to-b ${TIER_COLORS[notification.badge.tier]}`} />
        )}
      </div>
    </motion.div>
  );
}

export const GamificationToast: React.FC = () => {
  const { notifications, dismissNotification } = useGamification();
  // Big moments (level-up, milestone, gold/platinum badge) get a modal instead.
  const toastNotifications = notifications.filter((n) => !isGamificationMoment(n));

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col-reverse gap-3">
      <AnimatePresence mode="popLayout">
        {toastNotifications.slice(0, 3).map((notif) => (
          <ToastItem
            key={notif.id}
            notification={notif}
            onDismiss={() => dismissNotification(notif.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};
